/* Mirasand motion (test theme): GSAP ScrollTrigger choreography for the home page.
   Tuned to whisper: short travel, soft easing, no bounce or tilt theatrics.
   Transform/opacity only, entrances fire once, scrubs are continuous.
   Skips entirely for prefers-reduced-motion and in the theme editor. */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    var mobile = window.matchMedia('(max-width: 749px)').matches;

    /* Take over from Dawn's one-shot entrance animations so nothing animates twice. */
    document.querySelectorAll('.scroll-trigger').forEach(function (el) {
      el.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--cascade', 'scroll-trigger--offscreen', 'scroll-trigger--cancel');
    });

    /* Injected styles: progress bar, word masks, hero dust canvas. */
    var styleEl = document.createElement('style');
    styleEl.textContent =
      '.mira-progress{position:fixed;top:0;left:0;right:0;height:0.2rem;z-index:990;pointer-events:none;}' +
      '.mira-progress__bar{width:100%;height:100%;background:linear-gradient(to right,rgb(var(--mira-forest)),rgb(var(--mira-moss)));transform-origin:0 50%;transform:scaleX(0);}' +
      '.mira-word{display:inline-block;overflow:hidden;vertical-align:bottom;}' +
      '.mira-word>span{display:inline-block;}' +
      '.mira-sxh__dust{position:absolute;inset:0;z-index:2;pointer-events:none;}';
    document.head.appendChild(styleEl);

    /* Reading-progress bar. */
    var prog = document.createElement('div');
    prog.className = 'mira-progress';
    prog.setAttribute('aria-hidden', 'true');
    prog.innerHTML = '<div class="mira-progress__bar"></div>';
    document.body.appendChild(prog);
    gsap.to(prog.firstChild, {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: { start: 0, end: 'max', scrub: 0.3 }
    });

    /* Split a heading into masked words for the rise effect. */
    function splitWords(el) {
      if (!el || el.dataset.miraSplit) return [];
      el.dataset.miraSplit = '1';
      var words = el.textContent.split(/\s+/).filter(Boolean);
      el.textContent = '';
      var spans = [];
      words.forEach(function (w, i) {
        var mask = document.createElement('span');
        mask.className = 'mira-word';
        var inner = document.createElement('span');
        inner.textContent = w;
        mask.appendChild(inner);
        el.appendChild(mask);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
        spans.push(inner);
      });
      return spans;
    }

    /* Section headers: eyebrow rises, heading words rise out of masks, rule draws, lede follows. */
    gsap.utils.toArray('.mira-section-header').forEach(function (head) {
      var eyebrow = head.querySelector('.mira-eyebrow');
      var rule = head.querySelector('.mira-rule');
      var lede = head.querySelector('p');
      var words = splitWords(head.querySelector('h2'));
      var tl = gsap.timeline({
        scrollTrigger: { trigger: head, start: 'top 80%', once: true }
      });
      if (eyebrow) tl.from(eyebrow, { y: 10, autoAlpha: 0, duration: 0.5, ease: 'power2.out' }, 0);
      if (words.length) tl.from(words, { yPercent: 110, duration: 0.6, stagger: 0.04, ease: 'power2.out' }, 0.06);
      if (rule) tl.from(rule, { scaleX: 0, transformOrigin: '0 50%', duration: 0.55, ease: 'power2.inOut' }, 0.28);
      if (lede) tl.from(lede, { y: 12, autoAlpha: 0, duration: 0.5, ease: 'power2.out' }, 0.32);
    });

    /* Marquee: the whole strip skews faintly with scroll velocity, then settles. */
    var marquee = document.querySelector('.mira-marquee');
    if (marquee) {
      var skewProxy = { skew: 0 };
      var skewSetter = gsap.quickSetter(marquee, 'skewX', 'deg');
      var skewClamp = gsap.utils.clamp(-1.6, 1.6);
      ScrollTrigger.create({
        onUpdate: function (self) {
          var skew = skewClamp(self.getVelocity() / -560);
          if (Math.abs(skew) > Math.abs(skewProxy.skew)) {
            skewProxy.skew = skew;
            gsap.to(skewProxy, {
              skew: 0,
              duration: 0.8,
              ease: 'power3',
              overwrite: true,
              onUpdate: function () { skewSetter(skewProxy.skew); }
            });
          }
        }
      });
    }

    /* Trust strip: partner logo slides in, items pop one by one. */
    var trustPartner = document.querySelector('.mira-trust__partner');
    if (trustPartner) {
      gsap.from(trustPartner, {
        x: -14, autoAlpha: 0, duration: 0.6, ease: 'power2.out',
        scrollTrigger: { trigger: trustPartner, start: 'top 88%', once: true }
      });
    }
    var trustItems = gsap.utils.toArray('.mira-trust__item');
    if (trustItems.length) {
      gsap.from(trustItems, {
        y: 12, autoAlpha: 0, duration: 0.5, stagger: 0.07, ease: 'power2.out',
        scrollTrigger: { trigger: '.mira-trust__items', start: 'top 88%', once: true }
      });
    }

    /* Benefit cards: a quiet rise, settling together. */
    var cards = gsap.utils.toArray('.mira-benefits__card');
    if (cards.length) {
      gsap.set(cards, {
        y: 28,
        autoAlpha: 0,
        rotateZ: function (i) { return i % 2 ? 0.5 : -0.5; }
      });
      ScrollTrigger.batch(cards, {
        start: 'top 86%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { y: 0, autoAlpha: 1, rotateZ: 0, duration: 0.6, stagger: 0.07, ease: 'power2.out' });
        }
      });
    }

    /* Steps: numbers ease in, copy follows. */
    gsap.utils.toArray('.mira-steps__step').forEach(function (step, i) {
      var num = step.querySelector('.mira-steps__number');
      var body = step.querySelector('.mira-steps__body');
      var tl = gsap.timeline({
        scrollTrigger: { trigger: step, start: 'top 84%', once: true }
      });
      if (num) tl.from(num, { scale: 0.62, autoAlpha: 0, duration: 0.55, ease: 'power2.out' }, i * 0.05);
      if (body) tl.from(body, { y: 16, autoAlpha: 0, duration: 0.5, ease: 'power2.out' }, '<0.12');
    });

    /* Buy section: one gentle rise for the whole block. */
    var buyBlock = document.querySelector('.featured-product');
    if (buyBlock) {
      gsap.from(buyBlock, {
        y: 24, autoAlpha: 0, duration: 0.7, ease: 'power2.out',
        scrollTrigger: { trigger: buyBlock, start: 'top 80%', once: true }
      });
    }

    /* Science + CTA images: faint continuous parallax drift inside their frames. */
    gsap.utils.toArray('.image-with-text__media img, .banner__media img').forEach(function (img) {
      gsap.fromTo(img,
        { yPercent: -4, scale: 1.1 },
        {
          yPercent: 4,
          scale: 1.1,
          ease: 'none',
          scrollTrigger: {
            trigger: img.closest('.image-with-text__media, .banner__media') || img,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.4
          }
        });
    });

    /* Club: photo settles into place, perks tick in. */
    var clubMedia = document.querySelector('.mira-club__media');
    if (clubMedia) {
      gsap.from(clubMedia, {
        y: 26, rotateZ: -0.8, autoAlpha: 0, duration: 0.75, ease: 'power2.out',
        scrollTrigger: { trigger: '.mira-club', start: 'top 78%', once: true }
      });
    }
    var perks = gsap.utils.toArray('.mira-club__perks li');
    if (perks.length) {
      gsap.from(perks, {
        x: -16, autoAlpha: 0, duration: 0.5, stagger: 0.08, ease: 'power2.out',
        scrollTrigger: { trigger: '.mira-club__perks', start: 'top 85%', once: true }
      });
    }

    /* Reviews: cards arrive with a quiet rise. */
    var revs = gsap.utils.toArray('.mira-reviews__card');
    if (revs.length) {
      gsap.set(revs, {
        y: 30,
        autoAlpha: 0,
        rotateZ: function (i) { return i % 2 ? 0.5 : -0.5; }
      });
      ScrollTrigger.batch(revs, {
        start: 'top 88%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { y: 0, autoAlpha: 1, rotateZ: 0, duration: 0.6, stagger: 0.07, ease: 'power2.out' });
        }
      });
    }

    /* FAQ rows: quiet staggered rise. */
    var faqRows = gsap.utils.toArray('.collapsible-content .accordion');
    if (faqRows.length) {
      gsap.from(faqRows, {
        y: 16, autoAlpha: 0, duration: 0.5, stagger: 0.06, ease: 'power2.out',
        scrollTrigger: { trigger: faqRows[0], start: 'top 86%', once: true }
      });
    }

    /* CTA banner content. */
    var ctaBox = document.querySelector('.banner__box, .banner__content');
    if (ctaBox) {
      gsap.from(ctaBox, {
        y: 26, autoAlpha: 0, duration: 0.7, ease: 'power2.out',
        scrollTrigger: { trigger: ctaBox, start: 'top 85%', once: true }
      });
    }

    /* Hero dust: pale mineral specks drifting up inside the hero stage. */
    (function initDust() {
      var stage = document.querySelector('.mira-sxh__stage');
      if (!stage) return;
      var canvas = document.createElement('canvas');
      canvas.className = 'mira-sxh__dust';
      canvas.setAttribute('aria-hidden', 'true');
      stage.appendChild(canvas);
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = 0, h = 0, parts = [], running = false;
      var count = mobile ? 20 : 38;

      function size() {
        w = stage.clientWidth;
        h = stage.clientHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      function spawn() {
        parts = [];
        for (var i = 0; i < count; i++) {
          parts.push({
            x: Math.random() * w,
            y: Math.random() * h,
            r: 0.6 + Math.random() * 1.6,
            s: 0.1 + Math.random() * 0.28,
            d: (Math.random() - 0.5) * 0.16,
            a: 0.1 + Math.random() * 0.26,
            tw: Math.random() * 6.2832
          });
        }
      }
      function tick(t) {
        if (!running) return;
        ctx.clearRect(0, 0, w, h);
        for (var i = 0; i < parts.length; i++) {
          var p = parts[i];
          p.y -= p.s;
          p.x += p.d;
          if (p.y < -4) { p.y = h + 4; p.x = Math.random() * w; }
          if (p.x < -4) p.x = w + 4;
          else if (p.x > w + 4) p.x = -4;
          var glow = p.a * (0.7 + 0.3 * Math.sin(t / 900 + p.tw));
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, 6.2832);
          ctx.fillStyle = 'rgba(247, 243, 233, ' + glow.toFixed(3) + ')';
          ctx.fill();
        }
        requestAnimationFrame(tick);
      }
      function start() {
        if (!running) { running = true; requestAnimationFrame(tick); }
      }
      function stop() { running = false; }

      size();
      spawn();
      window.addEventListener('resize', function () { size(); spawn(); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting && !document.hidden) start();
          else stop();
        }).observe(stage);
      } else {
        start();
      }
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop();
        else if (stage.getBoundingClientRect().bottom > 0) start();
      });
    })();

    /* Re-measure once everything (images, video poster) has loaded. */
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  });
})();
